/* eslint-disable max-lines -- Figma-exported illustration; the length is SVG path/filter data, not logic */
import { FormattedMessage, useIntl } from 'react-intl';

import type { SVGProps } from 'react';

/**
 * Illustration for the Practice upgrade modal: a "Team settings" pill pointing
 * at role cards (Administrator / Assistant) and a permissions checklist.
 * Converted from the Figma export (upgrade-team-access.svg) so the labels can
 * be translated via react-intl. The two role descriptions render inside
 * <foreignObject> so translated copy reflows instead of relying on the
 * export's hardcoded per-line tspans.
 */
// fixed-palette Figma illustration; hardcoded hex is the baked artwork, not a tokenisation gap
export const UpgradeTeamAccessSvg = (props: SVGProps<SVGSVGElement>) => {
  const intl = useIntl();

  return (
    // direction="ltr": the artwork is composed left-to-right; without it, an
    // inherited dir="rtl" (Arabic) flips text-anchor's start side and every
    // label extends leftward from its authored x, clipping out of the canvas.
    <svg
      viewBox="0 0 613 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      direction="ltr"
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'Illustration of team settings showing the permissions of the Administrator and Assistant roles',
        id: 'PiJdYTomh7',
      })}
      {...props}
    >
      <g clipPath="url(#upgrade-team-access-clip0)">
        <g filter="url(#upgrade-team-access-filter0)">
          <rect
            x="64.4707"
            y="218.654"
            width="165"
            height="42.8345"
            rx="12"
            fill="#211217"
            shapeRendering="crispEdges"
          />
          <g clipPath="url(#upgrade-team-access-clip1)">
            <path
              d="M88.5541 232.139H88.2051C87.7843 232.139 87.3808 232.306 87.0833 232.604C86.7858 232.901 86.6186 233.305 86.6186 233.726V233.868C86.6183 234.147 86.5449 234.42 86.4057 234.661C86.2664 234.902 86.0663 235.102 85.8254 235.241L85.4843 235.439C85.2431 235.578 84.9695 235.652 84.6911 235.652C84.4126 235.652 84.139 235.578 83.8978 235.439L83.7788 235.376C83.4148 235.166 82.9823 235.109 82.5763 235.217C82.1703 235.326 81.824 235.591 81.6133 235.955L81.4388 236.256C81.2288 236.62 81.1718 237.053 81.2804 237.459C81.389 237.865 81.6542 238.211 82.0179 238.422L82.1369 238.501C82.3766 238.639 82.576 238.838 82.7152 239.077C82.8543 239.317 82.9284 239.588 82.9301 239.865V240.27C82.9312 240.549 82.8584 240.824 82.7191 241.067C82.5798 241.309 82.379 241.51 82.1369 241.65L82.0179 241.721C81.6542 241.932 81.389 242.278 81.2804 242.684C81.1718 243.09 81.2288 243.523 81.4388 243.887L81.6133 244.188C81.824 244.552 82.1703 244.817 82.5763 244.926C82.9823 245.034 83.4148 244.977 83.7788 244.767L83.8978 244.704C84.139 244.565 84.4126 244.491 84.6911 244.491C84.9695 244.491 85.2431 244.565 85.4843 244.704L85.8254 244.902C86.0663 245.041 86.2664 245.241 86.4057 245.482C86.5449 245.723 86.6183 245.996 86.6186 246.275V246.417C86.6186 246.838 86.7858 247.242 87.0833 247.539C87.3808 247.837 87.7843 248.004 88.2051 248.004H88.5541C88.9749 248.004 89.3784 247.837 89.6759 247.539C89.9734 247.242 90.1406 246.838 90.1406 246.417V246.275C90.1408 245.996 90.2143 245.723 90.3535 245.482C90.4927 245.241 90.6929 245.041 90.9338 244.902L91.2749 244.704C91.5161 244.565 91.7896 244.491 92.0681 244.491C92.3466 244.491 92.6202 244.565 92.8613 244.704L92.9803 244.767C93.3444 244.977 93.7769 245.034 94.1829 244.926C94.5889 244.817 94.9352 244.552 95.1459 244.188L95.3204 243.879C95.5304 243.515 95.5873 243.082 95.4788 242.676C95.3702 242.27 95.105 241.924 94.7413 241.713L94.6223 241.65C94.3802 241.51 94.1793 241.309 94.0401 241.067C93.9008 240.824 93.828 240.549 93.8291 240.27V239.873C93.828 239.594 93.9008 239.319 94.0401 239.076C94.1793 238.834 94.3802 238.633 94.6223 238.493L94.7413 238.422C95.105 238.211 95.3702 237.865 95.4788 237.459C95.5873 237.053 95.5304 236.62 95.3204 236.256L95.1459 235.955C94.9352 235.591 94.5889 235.326 94.1829 235.217C93.7769 235.109 93.3444 235.166 92.9803 235.376L92.8613 235.439C92.6202 235.578 92.3466 235.652 92.0681 235.652C91.7896 235.652 91.5161 235.578 91.2749 235.439L90.9338 235.241C90.6929 235.102 90.4927 234.902 90.3535 234.661C90.2143 234.42 90.1408 234.147 90.1406 233.868V233.726C90.1406 233.305 89.9734 232.901 89.6759 232.604C89.3784 232.306 88.9749 232.139 88.5541 232.139Z"
              stroke="#F9F4F1"
              strokeWidth="1.42782"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M88.3796 242.451C89.6939 242.451 90.7593 241.386 90.7593 240.071C90.7593 238.757 89.6939 237.692 88.3796 237.692C87.0653 237.692 85.9999 238.757 85.9999 240.071C85.9999 241.386 87.0653 242.451 88.3796 242.451Z"
              stroke="#F9F4F1"
              strokeWidth="1.42782"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#F9F4F1"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="16.6579"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="102.08" y="246.186">
              <FormattedMessage
                defaultMessage="Team settings"
                id="pLAFGLfIDF"
                description="Label on a mock navigation button in the Practice upgrade illustration, pointing at the team roles and permissions shown below it"
              />
            </tspan>
          </text>
        </g>
        <g filter="url(#upgrade-team-access-filter1)">
          <path
            d="M74.2832 339.123C74.2832 333.783 78.6116 329.455 83.9509 329.455H539.946C545.285 329.455 549.613 333.783 549.613 339.123V511.574C549.613 516.914 545.285 521.242 539.946 521.242H83.9509C78.6116 521.242 74.2832 516.914 74.2832 511.574V339.123Z"
            fill="white"
          />
          <text
            fill="#020617"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-geist-sans"
            fontSize="19.3355"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="100.064" y="375.503">
              <FormattedMessage
                defaultMessage="This user will have the following permissions:"
                id="BFUgPIxPKe"
                description="Heading of a mock permissions checklist in the Practice upgrade illustration, followed by items like 'Creating and preparing sessions'"
              />
            </tspan>
          </text>
          <g clipPath="url(#upgrade-team-access-clip2)">
            <path
              d="M107.315 408.822L108.927 410.433L112.149 407.211M117.789 408.822C117.789 413.272 114.182 416.879 109.732 416.879C105.283 416.879 101.676 413.272 101.676 408.822C101.676 404.373 105.283 400.766 109.732 400.766C114.182 400.766 117.789 404.373 117.789 408.822Z"
              stroke="#16A34A"
              strokeWidth="1.61129"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#64748B"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-geist-sans"
            fontSize="19.3355"
            letterSpacing="0em"
          >
            <tspan x="125.846" y="415.393">
              <FormattedMessage
                defaultMessage="Creating and preparing sessions."
                id="q/UDhr9edT"
                description="Item in a mock permissions checklist in the Practice upgrade illustration — the permission to create and prepare patient sessions"
              />
            </tspan>
          </text>
          <g clipPath="url(#upgrade-team-access-clip3)">
            <path
              d="M107.315 445.49L108.927 447.101L112.149 443.879M117.789 445.49C117.789 449.939 114.182 453.546 109.732 453.546C105.283 453.546 101.676 449.939 101.676 445.49C101.676 441.041 105.283 437.434 109.732 437.434C114.182 437.434 117.789 441.041 117.789 445.49Z"
              stroke="#16A34A"
              strokeWidth="1.61129"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#64748B"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-geist-sans"
            fontSize="19.3355"
            letterSpacing="0em"
          >
            <tspan x="125.846" y="452.061">
              <FormattedMessage
                defaultMessage="Generating documents"
                id="SdroUV4cZN"
                description="Item in a mock permissions checklist in the Practice upgrade illustration — the permission to generate clinical documents"
              />
            </tspan>
          </text>
          <rect
            x="126.039"
            y="393.961"
            width="317.874"
            height="28.5164"
            rx="7.14921"
            fill="url(#upgrade-team-access-paint0)"
          />
          <rect
            x="126.039"
            y="430.865"
            width="208.841"
            height="28.5164"
            rx="7.14921"
            fill="url(#upgrade-team-access-paint1)"
          />
        </g>
        <path
          d="M109.352 221.717C160.245 221.717 116.672 114.701 168.611 114.701"
          stroke="#755760"
          strokeWidth="0.697169"
          strokeLinecap="round"
        />
        <path
          d="M229.613 242.632H294.45"
          stroke="#755760"
          strokeWidth="0.697169"
          strokeLinecap="round"
        />
        <g filter="url(#upgrade-team-access-filter2)">
          <path
            d="M292.613 194.171C292.613 190.67 295.452 187.832 298.953 187.832H575.784C579.285 187.832 582.123 190.67 582.123 194.171V276.476C582.123 279.977 579.285 282.816 575.784 282.816H298.953C295.452 282.816 292.613 279.977 292.613 276.476V194.171Z"
            fill="white"
            shapeRendering="crispEdges"
          />
          <path
            d="M310.365 213.377C310.365 211.043 312.257 209.151 314.592 209.151H321.005C323.339 209.151 325.232 211.043 325.232 213.377V219.791C325.232 222.125 323.339 224.017 321.005 224.017H314.592C312.257 224.017 310.365 222.125 310.365 219.791V213.377Z"
            fill="#28030F"
          />
          <path
            d="M321.337 213.929L316.47 218.796L314.258 216.584"
            stroke="white"
            strokeWidth="1.59283"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text
            fill="#28030F"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-geist-sans"
            fontSize="14.8664"
            letterSpacing="0em"
          >
            <tspan x="331.148" y="221.787">
              <FormattedMessage
                defaultMessage="Administrator"
                id="1cFiJ2wSbD"
                description="Name of a team role on a mock role card in the Practice upgrade illustration — the role with full access to manage the team"
              />
            </tspan>
          </text>
          <foreignObject x="310.365" y="229.5" width="254" height="50">
            <div
              dir="auto"
              className="font-geist-sans"
              style={{ fontSize: '12.7427px', lineHeight: '18px', color: '#755760' }}
            >
              <FormattedMessage
                defaultMessage="For people who need total access — they can manage the team and its settings."
                id="DYVuDLhOm1"
                description="Description of the Administrator role on a mock role card in the Practice upgrade illustration"
              />
            </div>
          </foreignObject>
        </g>
        <g filter="url(#upgrade-team-access-filter3)">
          <path
            d="M169.281 72.7005C169.281 69.1992 172.12 66.3608 175.621 66.3608H568.101C571.602 66.3608 574.44 69.1992 574.44 72.7005V155.005C574.44 158.507 571.602 161.345 568.101 161.345H175.621C172.12 161.345 169.281 158.507 169.281 155.005V72.7005Z"
            fill="white"
            shapeRendering="crispEdges"
          />
          <path
            d="M187.033 91.9066C187.033 89.5724 188.925 87.6802 191.26 87.6802H197.673C200.007 87.6802 201.9 89.5724 201.9 91.9066V98.3202C201.9 100.654 200.007 102.547 197.673 102.547H191.26C188.925 102.547 187.033 100.654 187.033 98.3202V91.9066Z"
            fill="#28030F"
          />
          <path
            d="M198.005 92.4585L193.138 97.3255L190.926 95.1132"
            stroke="white"
            strokeWidth="1.59283"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text
            fill="#28030F"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-geist-sans"
            fontSize="14.8664"
            letterSpacing="0em"
          >
            <tspan x="207.816" y="100.317">
              <FormattedMessage
                defaultMessage="Assistant"
                id="F7Otf5NzS1"
                description="Name of a team role on a mock role card in the Practice upgrade illustration — the support-staff role that helps with documentation without editing core notes"
              />
            </tspan>
          </text>
          <foreignObject x="187.033" y="108" width="370" height="50">
            <div
              dir="auto"
              className="font-geist-sans"
              style={{ fontSize: '12.7427px', lineHeight: '18px', color: '#755760' }}
            >
              <FormattedMessage
                defaultMessage="For support staff who need to help with documentation and session management without modifying core session notes."
                id="Pf16qrbJJ+"
                description="Description of the Assistant role on a mock role card in the Practice upgrade illustration"
              />
            </div>
          </foreignObject>
        </g>
        <path
          d="M41.3764 211.825L48.1934 211.825C50.5166 211.825 52.3998 209.942 52.3998 207.619L52.3998 200.802L54.0566 200.802L54.0566 207.619C54.0566 209.942 55.9398 211.825 58.263 211.825L65.0801 211.825L65.0801 213.482L58.263 213.482C55.9398 213.482 54.0566 215.365 54.0566 217.688L54.0566 224.505L52.3998 224.505L52.3998 217.688C52.3998 215.365 50.5166 213.482 48.1934 213.482L41.3764 213.482L41.3764 211.825Z"
          fill="#28030F"
        />
      </g>
      <defs>
        <filter
          id="upgrade-team-access-filter0"
          x="42.1613"
          y="204.711"
          width="209.619"
          height="87.4533"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="8.36602" />
          <feGaussianBlur stdDeviation="11.1547" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="upgrade-team-access-filter1"
          x="30.8734"
          y="302.324"
          width="562.15"
          height="278.607"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="16.2787" />
          <feGaussianBlur stdDeviation="21.7049" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="upgrade-team-access-filter2"
          x="267.467"
          y="164.257"
          width="339.802"
          height="145.277"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.57163" />
          <feGaussianBlur stdDeviation="12.573" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="upgrade-team-access-filter3"
          x="144.135"
          y="42.7864"
          width="455.45"
          height="145.277"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.57163" />
          <feGaussianBlur stdDeviation="12.573" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <linearGradient
          id="upgrade-team-access-paint0"
          x1="126.039"
          y1="408.219"
          x2="443.913"
          y2="408.219"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F4E7DD" />
          <stop offset="1" stopColor="#F9F4F1" />
        </linearGradient>
        <linearGradient
          id="upgrade-team-access-paint1"
          x1="126.039"
          y1="445.123"
          x2="334.88"
          y2="445.123"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F4E7DD" />
          <stop offset="1" stopColor="#F9F4F1" />
        </linearGradient>
        <clipPath id="upgrade-team-access-clip0">
          <rect width="613" height="460" fill="white" />
        </clipPath>
        <clipPath id="upgrade-team-access-clip1">
          <path
            d="M78.8613 232.5C78.8613 231.424 79.733 230.553 80.8084 230.553H95.9519C97.0272 230.553 97.8989 231.424 97.8989 232.5V247.643C97.8989 248.719 97.0272 249.59 95.9519 249.59H80.8084C79.733 249.59 78.8613 248.719 78.8613 247.643V232.5Z"
            fill="white"
          />
        </clipPath>
        <clipPath id="upgrade-team-access-clip2">
          <rect
            width="19.3355"
            height="19.3355"
            fill="white"
            transform="translate(100.064 399.154)"
          />
        </clipPath>
        <clipPath id="upgrade-team-access-clip3">
          <rect
            width="19.3355"
            height="19.3355"
            fill="white"
            transform="translate(100.064 435.822)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
